import { useState } from "react";
import { App, Button, Form, Input, Mentions, Modal, Select, Space, Tag, Typography } from "antd";
import { FormOutlined, PlusOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { DataTable, useTableParams } from "../../components/DataTable";
import { RelativeTime } from "../../components/format";
import { api } from "../../services/api";
import { useAuth } from "../../services/auth";
import { useCharacter } from "../../services/character";
import type { ContentContextOptions, ContentDraftDetail } from "./types";

const FILTERS = ["status", "search"];

interface ContentDraftRow {
  id: string;
  characterId: string;
  characterName: string;
  characterVersion: number;
  brief: string;
  status: "draft" | "finalized";
  currentRevision: { id: string; sequence: number; createdAt: string } | null;
  updatedByName: string | null;
  updatedAt: string;
}

interface CreateForm {
  brief: string;
  songId?: string;
  planDate?: string;
  momentIds?: string[];
}

export function ContentDraftsPage() {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { can } = useAuth();
  const { characterId } = useCharacter();
  const [form] = Form.useForm<CreateForm>();
  const selectedSongId = Form.useWatch("songId", form);
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const { params, setParam } = useTableParams(FILTERS);

  const context = useQuery({
    queryKey: ["contentContextOptions", characterId],
    queryFn: () => api.get<ContentContextOptions>("/api/v1/admin/content-drafts/context-options", { characterId }),
    enabled: open && Boolean(characterId),
  });
  const selectedSong = (context.data?.songs ?? []).find((song) => song.id === selectedSongId) ?? null;

  const clearSongMention = () => {
    const brief = form.getFieldValue("brief") ?? "";
    form.setFieldsValue({
      brief: selectedSong ? brief.replace(`@${selectedSong.title}`, "").replace(/\s{2,}/g, " ").trim() : brief,
      songId: undefined,
    });
  };

  const columns: ColumnsType<ContentDraftRow> = [
    {
      title: "需求",
      dataIndex: "brief",
      width: 420,
      render: (value: string, row) => (
        <Space direction="vertical" size={2}>
          <Link to={`/content/${row.id}`}><Typography.Text strong>{value}</Typography.Text></Link>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {row.characterName} · 角色版本 v{row.characterVersion}
          </Typography.Text>
        </Space>
      ),
    },
    {
      title: "状态",
      dataIndex: "status",
      width: 100,
      render: (value: ContentDraftRow["status"]) => (
        <Tag color={value === "finalized" ? "green" : "blue"}>{value === "finalized" ? "已定稿" : "草稿"}</Tag>
      ),
    },
    {
      title: "当前版本",
      width: 100,
      render: (_, row) => row.currentRevision ? `#${row.currentRevision.sequence}` : "未生成",
    },
    {
      title: "最近更新",
      width: 170,
      render: (_, row) => (
        <Space direction="vertical" size={0}>
          <RelativeTime value={row.updatedAt} />
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>{row.updatedByName ?? "管理员"}</Typography.Text>
        </Space>
      ),
    },
    {
      title: "操作",
      width: 90,
      render: (_, row) => <Link to={`/content/${row.id}`}>打开</Link>,
    },
  ];

  const create = async (values: CreateForm) => {
    if (!characterId) return;
    setCreating(true);
    try {
      const draft = await api.post<ContentDraftDetail>("/api/v1/admin/content-drafts", {
        characterId,
        brief: values.brief,
        songId: values.songId ?? null,
        planDate: values.planDate ?? null,
        momentIds: values.momentIds ?? [],
      });
      message.success("草稿已创建");
      setOpen(false);
      form.resetFields();
      void queryClient.invalidateQueries({ queryKey: ["/api/v1/admin/content-drafts"] });
      navigate(`/content/${draft.id}`);
    } catch (error) {
      message.error((error as Error).message);
    } finally {
      setCreating(false);
    }
  };

  return (
    <>
      <DataTable<ContentDraftRow>
        path="/api/v1/admin/content-drafts"
        columns={columns}
        fixedParams={{ characterId }}
        filterKeys={FILTERS}
        title={<Space><FormOutlined />内容发布</Space>}
        extra={can("content.write") ? (
          <Button type="primary" icon={<PlusOutlined />} disabled={!characterId} onClick={() => setOpen(true)}>
            新建草稿
          </Button>
        ) : null}
        toolbar={(
          <Space wrap>
            <Input.Search
              allowClear
              placeholder="搜索需求"
              defaultValue={String(params.search ?? "")}
              onSearch={(value) => setParam("search", value)}
              style={{ width: 260 }}
            />
            <Select
              allowClear
              placeholder="全部状态"
              value={params.status as string | undefined}
              onChange={(value) => setParam("status", value)}
              options={[{ value: "draft", label: "草稿" }, { value: "finalized", label: "已定稿" }]}
              style={{ width: 130 }}
            />
            {!characterId && <Typography.Text type="secondary">先在顶部选择一个角色，才能新建草稿</Typography.Text>}
          </Space>
        )}
        tableLayout="fixed"
        disableHorizontalScroll
        onRow={(row) => ({ onDoubleClick: () => navigate(`/content/${row.id}`) })}
      />

      <Modal
        title="新建发布文案草稿"
        open={open}
        onCancel={() => setOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={creating}
        okText="创建草稿"
        destroyOnClose
      >
        <Form<CreateForm> form={form} layout="vertical" onFinish={create} preserve={false}>
          <Form.Item
            name="brief"
            label="这次想发布什么"
            rules={[{ required: true, message: "简单写一下主题、事实和希望表达的感觉" }, { max: 4000 }]}
          >
            <Mentions
              rows={6}
              maxLength={4000}
              placeholder="例如：想分享 @歌曲名 的录制感受。输入 @ 从歌曲库选择，最多关联一首。"
              options={selectedSongId ? [] : (context.data?.songs ?? []).map((song) => ({
                key: song.id,
                value: song.title,
                label: `${song.title}${song.originalArtists.length ? ` · 原唱 ${song.originalArtists.join(" / ")}` : ""} · ${song.hasLyrics ? `歌词 ${song.lyricsLength} 字` : "暂无歌词"}`,
              }))}
              onSelect={(option) => form.setFieldValue("songId", String(option.key))}
              onChange={(value) => {
                if (selectedSong && !value.includes(`@${selectedSong.title}`)) {
                  form.setFieldValue("songId", undefined);
                }
              }}
            />
          </Form.Item>
          <Form.Item name="songId" hidden><Input /></Form.Item>
          <div style={{ marginTop: -12, marginBottom: 18 }}>
            {selectedSong ? (
              <Tag color="purple" closable onClose={(event) => { event.preventDefault(); clearSongMention(); }}>
                已关联歌曲 @{selectedSong.title} · {selectedSong.hasLyrics ? `${selectedSong.lyricsLength} 字歌词` : "暂无歌词"}
              </Tag>
            ) : (
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                尚未关联歌曲；必须从 @ 自动补全中点选，关联成功后这里会出现标签。
              </Typography.Text>
            )}
          </div>
          <Form.Item name="planDate" label="可选：今日设定">
            <Select
              allowClear
              loading={context.isLoading}
              placeholder="默认不引用"
              options={(context.data?.plans ?? []).map((plan) => ({
                value: plan.dateString,
                label: `${plan.dateString}${plan.title ? ` · ${plan.title}` : ""} · ${plan.plan}`,
              }))}
            />
          </Form.Item>
          <Form.Item name="momentIds" label="可选：共享梦灵动态（最多 3 条）">
            <Select
              mode="multiple"
              maxCount={3}
              allowClear
              loading={context.isLoading}
              placeholder="默认不引用"
              options={(context.data?.moments ?? []).map((moment) => ({
                value: moment.id,
                label: `${moment.title ?? "未命名动态"} · ${moment.factSummary}`,
              }))}
            />
          </Form.Item>
          <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 0 }}>
            只会使用当前角色设定与这里明确选中的歌曲、今日设定和梦灵动态；歌曲歌词由服务端从歌曲库读取，不会读取用户聊天、关系日记或记忆。
          </Typography.Paragraph>
        </Form>
      </Modal>
    </>
  );
}
